import { useProvider } from "@/hooks/useProvider";
import { useVersion } from "@/hooks/useVersion";
import { useParams } from "react-router-dom";
import { PreviewHeader } from "./PreviewHeader";
import { PreviewCategoryChannelTable } from "@/features/versions/components/preview/PreviewCategoryChannelTable";
import { ErrorDisplay } from "@/components/common/ErrorBoundary";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { PreviewPlanLegend } from "./PreviewPlanLegend";
import { PreviewPackageLegend } from "./PreviewPackageLegend";
import { PreviewTagLegend } from "./PreviewTagLegend";
import { usePreviewFilterStore } from "@/stores/previewFilterStore";

export function VersionPreviewPage() {
  const { providerId, versionId } = useParams();
  const { data: provider, isLoading: providerLoading } = useProvider(Number(providerId));
  const { data: version, isLoading: versionLoading } = useVersion({
    providerId: Number(providerId),
    versionId: Number(versionId)
  });
  const { showPlans, showPackages, showTags } = usePreviewFilterStore();

  if (providerLoading || versionLoading) {
    return <LoadingSpinner />;
  }

  if (!provider || !version) {
    return <ErrorDisplay error="Provider or version not found" />;
  }

  // Ensure version has all required properties
  const safeVersion = {
    ...version,
    description: version.description || null,
    effective_date: version.effective_date || null,
    published_at: version.published_at || null,
    parent_version_id: null,
    source_version_id: null,
    region_id: version.region_id || 1
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <PreviewHeader
        provider={provider}
        version={safeVersion}
      />
      <div className="flex-1">
        <div className="container mx-auto max-w-[1200px] space-y-8 py-8">
          {showPlans && (
            <PreviewPlanLegend
              providerId={Number(providerId)}
              versionId={Number(versionId)}
            />
          )}
          {showPackages && (
            <PreviewPackageLegend
              providerId={Number(providerId)}
              versionId={Number(versionId)}
              regionId={safeVersion.region_id}
            />
          )}
          {showTags && safeVersion.region_id && (
            <PreviewTagLegend
              providerId={Number(providerId)}
              versionId={Number(versionId)}
              regionId={safeVersion.region_id}
            />
          )}
          <PreviewCategoryChannelTable
            providerId={Number(providerId)}
            versionId={Number(versionId)}
            hasSequenceNumbers={provider.has_sequence_numbers}
          />
        </div>
      </div>
    </div>
  );
}
